// Time off uses the calendar's local zone; saved intervals keep that provenance.
const formatters = new Map();
export function calendarTimeZone() { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
function formatter(timeZone) {
  if (!formatters.has(timeZone)) formatters.set(timeZone,new Intl.DateTimeFormat('en-CA',{timeZone,year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',hourCycle:'h23'}));
  return formatters.get(timeZone);
}
function parts(value,timeZone) { return Object.fromEntries(formatter(timeZone).formatToParts(new Date(value)).filter(p=>p.type!=='literal').map(p=>[p.type,p.value])); }
export function timeOffLocalInput(value,timeZone=calendarTimeZone()) {
  try { const p=parts(value,timeZone);return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`; } catch { return ''; }
}
export function timeOffShiftDate(date,days) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
  const d=new Date(date+'T12:00:00Z');if(!Number.isFinite(d.getTime())||d.toISOString().slice(0,10)!==date)return '';
  d.setUTCDate(d.getUTCDate()+days);return d.toISOString().slice(0,10);
}
export function timeOffTimeChoices(value,timeZone=calendarTimeZone()) {
  const m=/^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  if(!m || !timeOffShiftDate(value.slice(0,10),0))return [];
  const wall=Date.UTC(Number(m[1]),Number(m[2])-1,Number(m[3]),Number(m[4]),Number(m[5]));
  try {
    const offsets=new Set([-2,-1,0,1,2].map(day=>{
      const sample=wall+day*86400000,p=parts(sample,timeZone);
      return Date.UTC(Number(p.year),Number(p.month)-1,Number(p.day),Number(p.hour),Number(p.minute))-sample;
    }));
    return [...new Set([...offsets].map(offset=>wall-offset).filter(ms=>timeOffLocalInput(ms,timeZone)===value))].sort((a,b)=>a-b).map(ms=>({
      value:new Date(ms).toISOString(),
      label:new Intl.DateTimeFormat('en-US',{timeZone,hour:'numeric',minute:'2-digit',timeZoneName:'shortOffset'}).format(new Date(ms)),
    }));
  }catch{return [];}
}
export function timeOffResolveInput(value,timeZone=calendarTimeZone(),choice='',originalIso=null) {
  const choices=timeOffTimeChoices(value,timeZone);
  if(originalIso && timeOffLocalInput(originalIso,timeZone)===value){
    const originalMs=new Date(originalIso).getTime();
    const originalChoice=choices.find(c=>originalMs>=new Date(c.value).getTime()&&originalMs<new Date(c.value).getTime()+60000);
    if(originalChoice && (!choice || choice===originalChoice.value))return originalIso;
  }
  if(choice && choices.some(c=>c.value===choice))return choice;
  return choices.length===1?choices[0].value:null;
}
export function timeOffAllDayRange(first,last,timeZone=calendarTimeZone()) {
  const after=timeOffShiftDate(last,1);
  if(!after || !timeOffShiftDate(first,0) || last<first)return null;
  const startLocal=first+'T00:00',endLocal=after+'T00:00';
  const startsAt=timeOffResolveInput(startLocal,timeZone),endsAt=timeOffResolveInput(endLocal,timeZone);
  return startsAt&&endsAt&&new Date(endsAt)>new Date(startsAt)?{startLocal,endLocal,startsAt,endsAt}:null;
}
export function timeOffPresentation(interval,viewerZone=calendarTimeZone()) {
  const startMs=new Date(interval.startsAt).getTime(),endMs=new Date(interval.endsAt).getTime();
  if(!Number.isFinite(startMs)||!Number.isFinite(endMs)||endMs<=startMs)return null;
  const localStart=timeOffLocalInput(interval.startsAt,viewerZone),localEnd=timeOffLocalInput(interval.endsAt,viewerZone);
  const inferred=localStart.endsWith('T00:00')&&localEnd.endsWith('T00:00')&&startMs%1000===0&&endMs%1000===0;
  const allDay=typeof interval.allDay==='boolean'?interval.allDay:inferred;
  let zone=interval.timeZone || viewerZone;try{formatter(zone);}catch{zone=viewerZone;}
  const dateZone=typeof interval.allDay==='boolean'&&interval.timeZone?zone:viewerZone;
  const first=timeOffLocalInput(interval.startsAt,dateZone).slice(0,10),exclusive=timeOffLocalInput(interval.endsAt,dateZone).slice(0,10);
  const full=new Intl.DateTimeFormat('en-US',{timeZone:zone,year:'numeric',month:'short',day:'numeric',hour:'numeric',minute:'2-digit',timeZoneName:'short'});
  const short=new Intl.DateTimeFormat('en-US',{timeZone:viewerZone,hour:'numeric',minute:'2-digit'});
  const dateLabel=date=>new Intl.DateTimeFormat('en-US',{timeZone:'UTC',year:'numeric',month:'short',day:'numeric'}).format(new Date(date+'T12:00:00Z'));
  const last=timeOffShiftDate(exclusive,-1);
  // The request card's label: a same-day request names its date once ("Thu, Oct 29 · 2:15 – 5:00 PM EDT").
  const thisYear=new Intl.DateTimeFormat('en-US',{timeZone:zone,year:'numeric'});
  const year=thisYear.format(startMs)===thisYear.format(Date.now())?{}:{year:'numeric'};
  const weekdayDate=date=>new Intl.DateTimeFormat('en-US',{timeZone:'UTC',weekday:'short',month:'short',day:'numeric',...year}).format(new Date(date+'T12:00:00Z'));
  const sameDay=timeOffLocalInput(interval.startsAt,zone).slice(0,10)===timeOffLocalInput(interval.endsAt,zone).slice(0,10);
  const requestLabel=allDay
    ?`${weekdayDate(first)}${last!==first?' – '+weekdayDate(last):''} · All day`
    :sameDay
      ?`${new Intl.DateTimeFormat('en-US',{timeZone:zone,weekday:'short',month:'short',day:'numeric',...year}).format(startMs)} · ${new Intl.DateTimeFormat('en-US',{timeZone:zone,hour:'numeric',minute:'2-digit',timeZoneName:'short'}).formatRange(startMs,endMs)}`
      :new Intl.DateTimeFormat('en-US',{timeZone:zone,weekday:'short',month:'short',day:'numeric',hour:'numeric',minute:'2-digit',timeZoneName:'short',...year}).formatRange(startMs,endMs);
  return {
    requestLabel,
    allDay,
    calendarStart:allDay?first:interval.startsAt,
    calendarEnd:allDay?exclusive:interval.endsAt,
    timeLabel:allDay?'All day':localStart.slice(0,10)===localEnd.slice(0,10)?`${short.format(startMs)} - ${short.format(endMs)}`:`${new Intl.DateTimeFormat('en-US',{timeZone:viewerZone,month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}).format(startMs)} - ${new Intl.DateTimeFormat('en-US',{timeZone:viewerZone,month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}).format(endMs)}`,
    detailLabel:allDay?`${dateLabel(first)}${last!==first?' - '+dateLabel(last):''} · All day (${zone})`:`${full.format(startMs)} - ${full.format(endMs)}`,
  };
}
